<template>
  <!-- Página Institucional - quando USE_INSTITUTIONAL_HOME é true -->
  <InstitutionalHome v-if="useInstitutionalHome" />
  
  <!-- Homes normais - quando USE_INSTITUTIONAL_HOME é false -->
  <HomeCasaSoftware v-else-if="isCasaDoSoftware" :home-theme="homeTheme" />
  <HomeCenterKeys v-else-if="isLicencasDigitais" :only-best-sellers="true" :home-theme="homeTheme" />
  <HomeInternational v-else-if="isInternational" />
  <HomeCenterKeys v-else :only-best-sellers="false" :home-theme="homeTheme" />


</template>

<script setup lang="ts">
import InstitutionalHome from '~/components/InstitutionalHome.vue'
import HomeInternational from '~/components/HomeInternational.vue'
import { useJsonLd } from '~/composables/useJsonLd'
import { getCasaHomeJsonLdBundle } from '~/services/casaJsonLd'
import { useHomeTheme } from '~/composables/useHomeTheme'

const config = useRuntimeConfig()

// A mesma variável controla a home institucional e os redirecionamentos.
const useInstitutionalHome =
  String(config.public.redirectAllToHome || '')
    .trim()
    .toLowerCase() === 'true'

// Remove cabeçalho, menu, carrinho e rodapé da loja no modo institucional.
setPageLayout(useInstitutionalHome ? 'institutional' : 'default')

const { homeTheme } = useHomeTheme()
const storeSlug = computed(() => String((config.public as any)?.storeSlug || '').trim())

const baseUrl = useSiteUrl()

const url = useRequestURL()

const reqHostname = computed(() => {
  if (process.server) {
    try {
      return String(url.hostname || '').toLowerCase()
    } catch {
      return ''
    }
  }
  try {
    return String(window.location.hostname || '').toLowerCase()
  } catch {
    return ''
  }
})

const isEN = computed(() => {
  const h = reqHostname.value
  return h.endsWith('.store') || h.startsWith('en.')
})

const CASA_HOME_TITLE = computed(() =>
  isEN.value
    ? 'Original Windows and Office Licenses | Casa do Software'
    : 'Licenças Originais Windows e Office | Casa do Software'
)

const CASA_HOME_DESCRIPTION = computed(() =>
  isEN.value
    ? 'Buy genuine Windows 10, 11 and Office licenses with instant delivery and expert support.'
    : 'Compre licenças digitais originais Windows 10, 11 e Office com entrega imediata e suporte técnico.'
)

const { canonicalUrl: homeCanonical, hreflangLinks: homeHreflang } = useSeoLocale({ pageType: 'home' })
useHead(() => ({
  link: [
    { rel: 'canonical', href: homeCanonical.value },
    ...(homeHreflang.value as any[])
  ]
}))

const host = computed(() => {
  if (process.server) {
    try {
      const url = useRequestURL()
      if (url?.host) return String(url.host).toLowerCase()
    } catch {
      // ignore
    }

    try {
      const headers = useRequestHeaders(['x-forwarded-host', 'x-original-host', 'host']) as Record<
        string,
        string | undefined
      >
      const raw = headers?.['x-forwarded-host'] || headers?.['x-original-host'] || headers?.host || ''
      const first = String(raw).split(',')[0]?.trim()
      return String(first || '').toLowerCase()
    } catch {
      return ''
    }
  }

  return String(window.location.host || '').toLowerCase()
})

const normalizedHost = computed(() => {
  const h0 = String(host.value || '').trim().toLowerCase()
  const h1 = h0.replace(/^https?:\/\//, '')
  const h2 = h1.replace(/\/.*/, '')
  const h3 = h2.replace(/:\d+$/, '')
  const h4 = h3.replace(/^www\./, '')
  return h4.replace(/\.$/, '')
})

const isCasaDoSoftware = computed(() => {
  if (normalizedHost.value.includes('casadosoftware.com.br')) return true
  if (normalizedHost.value.endsWith('.store') && !normalizedHost.value.includes('globalsoftware.store')) return true
  return storeSlug.value === 'casadosoftware'
})

const isLicencasDigitais = computed(() => {
  if (normalizedHost.value.includes('licencasdigitais.com.br')) return true
  return storeSlug.value === 'licencasdigitais'
})

const isInternational = computed(() => {
  // Localhost tratado como domínio internacional (GVGmall)
  if (normalizedHost.value.includes('localhost') || normalizedHost.value.includes('127.0.0.1')) return true
  if (normalizedHost.value.includes('globalsoftware.store')) return true
  if (normalizedHost.value.includes('gvgmall.co')) return true
  return storeSlug.value === 'international'
})

console.log('[index.vue] normalizedHost:', normalizedHost.value)
console.log('[index.vue] storeSlug:', storeSlug.value)
console.log('[index.vue] isInternational:', isInternational.value)
console.log('[index.vue] isCasaDoSoftware:', isCasaDoSoftware.value)
console.log('[index.vue] isLicencasDigitais:', isLicencasDigitais.value)

const applyCasaSeo = computed(() => {
  if (storeSlug.value && storeSlug.value !== 'casadosoftware') return false
  if (isCasaDoSoftware.value) return true
  if (process.server && !normalizedHost.value) return true
  return false
})

if (applyCasaSeo.value) {
  const ogImage = 'https://pub-388810139d004c3eb59d2d54c6e92aa7.r2.dev/uploads/Logo%20Marca%201.png'

  useSeoMeta({
    title: CASA_HOME_TITLE.value,
    description: CASA_HOME_DESCRIPTION.value,
    ogTitle: CASA_HOME_TITLE.value,
    ogDescription: CASA_HOME_DESCRIPTION.value,
    ogImage,
    ogType: 'website',
    ogUrl: homeCanonical.value,
    twitterTitle: CASA_HOME_TITLE.value,
    twitterDescription: CASA_HOME_DESCRIPTION.value,
    twitterImage: ogImage
  })
}


useJsonLd(
  () => {
    if (!applyCasaSeo.value) return null

    let origin = ''
    let jsonLdHost = normalizedHost.value
    if (process.server) {
      try {
        const url = useRequestURL()
        origin = String(url.origin || '').trim()
        jsonLdHost = String(url.hostname || normalizedHost.value || '').trim().toLowerCase()
      } catch {
        // ignore
      }
    } else {
      try {
        origin = String(window.location.origin || '')
      } catch {
        // ignore
      }

      try {
        jsonLdHost = String(window.location.hostname || '').toLowerCase()
      } catch {
        // ignore
      }
    }

    return getCasaHomeJsonLdBundle({ host: jsonLdHost, origin })
  },
  'jsonld-casa-home'
)

const debugHost = computed(() => {
  if (process.server) return false
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get('debugHost') === '1'
  } catch {
    return false
  }
})
</script>
